import React, { useState } from "react";
import { Form, Formik } from "formik";
import {
  DefaultButton,
  DefaultPrimaryButton,
  InputWithLabel,
} from "../../components/micro";
import Breadcrumb from "../../components/common/Breadcrumb";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { editUsers } from "../../redux/user/slice";

const EditUser = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const userData = location?.state?.user;
  const isEditMode = location?.state?.mode;
  const [formValues, setFormValues] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleEditClick = () => setIsEditing(true);
  const handleCancelClick = () => navigate("/");

  const handleConfirm = () => {
    setIsLoading(true);

    const payload = {
      userId: userData?._id,
      firstName: formValues?.name,
      lastName: formValues?.lastname,
      email: formValues?.email,
      googleAuthId: userData?.googleAuthId,
      appleAuthId: userData?.appleAuthId,
      authToken: userData?.authToken,
      authType: userData?.authType,
    };

    const action = editUsers(payload);

    dispatch(action)
      .then((res) => {
        if (res.type === "editUsers/fulfilled") {
          navigate("/");
        }
      })
      .finally(() => {
        setIsLoading(false);
        setShowConfirmationModal(false);
      });
  };

  return (
    <>
      <div className="space-y-5">
        <div>
          <h6 className="font-Montserrat font-medium text-xl mb-3">
            Edit User
          </h6>
          <Breadcrumb
            breadCrumbParent={"User"}
            breadCrumbActive={"Edit User"}
            breadCrumbTitleKey={"/"}
            classes=""
          />
        </div>
        <div className="bg-white shadow-md rounded-lg p-6">
          <Formik
            initialValues={{
              name: userData?.firstName || "",
              lastname: userData?.lastName || "",
              email: userData?.email || "",
              habits:
                userData?.habits?.map((habit) => ({
                  title: habit?.name || "",
                  icon: habit?.icon || "",
                })) || [],
            }}
            validationSchema={Yup.object().shape({
              name: Yup.string()
                .trim()
                .nullable()
                .required("Please enter movement name"),
              lastname: Yup.string()
                .trim()
                .nullable()
                .required("Please enter lastname"),
              email: Yup.string().required("Please enter email"),
              habits: Yup.array().of(
                Yup.object().shape({
                  title: Yup.string()
                    .trim()
                    .nullable()
                    .required("Please enter habits name"),
                })
              ),
            })}
            onSubmit={(values) => {
              setFormValues(values);
              setShowConfirmationModal(true);
            }}
          >
            {({ handleChange, handleBlur, values, errors, touched }) => (
              <Form>
                <div className="grid grid-cols-2 gap-6 mb-4">
                  <InputWithLabel
                    idFromName="name"
                    name="name"
                    label="First Name*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    errors={errors.name && touched.name}
                    errorsText={errors.name}
                    disabled={isEditMode === "edit" && !isEditing}
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black  border hover:border-themeBtn"
                    } ${
                      errors.name && touched.name
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    }  transition duration-300 ease-in-out`}
                  />
                  <InputWithLabel
                    idFromName="lastname"
                    name="lastname"
                    label="Last Name*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.lastname}
                    errors={errors.lastname && touched.lastname}
                    errorsText={errors.lastname}
                    disabled={isEditMode === "edit" && !isEditing}
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black  border hover:border-themeBtn"
                    } ${
                      errors.lastname && touched.lastname
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    }  transition duration-300 ease-in-out`}
                  />
                  <InputWithLabel
                    idFromName="email"
                    name="email"
                    label="Email*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    errors={errors.email && touched.email}
                    errorsText={errors.email}
                    disabled
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-textGrey  border border-dashed"
                    }${
                      !isEditing ? "disabled:bg-zinc-300/20" : "bg-transparent"
                    } 
                    rounded border-borderTheme/80 appearance-none disabled-opacity-80 peer`}
                  />
                </div>

                <div className="flex justify-end gap-3 mt-3">
                  <DefaultPrimaryButton
                    type="button"
                    value={isEditMode === "edit" ? "Back" : "Cancel"}
                    onClick={handleCancelClick}
                  />
                  {isEditMode === "edit" ? (
                    isEditing ? (
                      <>
                        <DefaultButton
                          type="submit"
                          value="Save"
                          onClick={(e) => {}}
                        />
                      </>
                    ) : (
                      <DefaultButton
                        value="Edit"
                        onClick={handleEditClick}
                        type="submit"
                      />
                    )
                  ) : (
                    <>
                      <DefaultButton type="submit" value="confirm" />
                    </>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
        {showConfirmationModal && (
          <ConfirmationModel
            confirmText={"Edit"}
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={handleConfirm}
            isLoading={isLoading}
          />
        )}
      </div>
    </>
  );
};

export default EditUser;
